/**
 * Reports → Incident Reporting subpage.
 * Shows a tactical incident-type selector. Selecting and continuing
 * routes the officer into the incident creation flow.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { INCIDENT_TYPES } from "@/lib/incident-types";

export const Route = createFileRoute("/_authenticated/reports/incident")({
  component: IncidentReportingPage,
  validateSearch: (s: Record<string, unknown>) => ({
    subtype: typeof s.subtype === "string" ? s.subtype : undefined,
  }),
  head: () => ({
    meta: [{ title: "Incident Reporting — Reports — MissionReady" }],
  }),
});

function IncidentReportingPage() {
  const navigate = useNavigate();
  const { subtype: prefilledSubtype } = Route.useSearch();
  const [type, setType] = useState<string>(prefilledSubtype ?? "");

  const onContinue = () => {
    if (!type) return;
    void navigate({ to: "/incidents/new", search: { subtype: type } });
  };

  return (
    <div className="px-4 pb-28 pt-6 md:px-8 md:py-10">
      <div className="mx-auto max-w-2xl">
        <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2 text-muted-foreground hover:text-foreground">
          <Link to="/reports">
            <ArrowLeft className="mr-1 h-4 w-4" /> Reports
          </Link>
        </Button>

        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
          Incident Reporting
        </p>
        <h1 className="mt-1 font-display text-2xl font-bold md:text-3xl">Document an incident</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Select the incident type to begin documentation.
        </p>

        <div className="mt-6 rounded-2xl border border-[#C0C0C0]/15 bg-card p-5 shadow-card">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary ring-1 ring-inset ring-[#C0C0C0]/15">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-base font-semibold text-foreground">
                Incident Type <span className="text-destructive" aria-hidden="true">*</span>
                <span className="sr-only">(required)</span>
              </div>
              <p className="text-xs text-muted-foreground">Choose the closest match.</p>
            </div>
          </div>

          <div className="mt-4">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger aria-label="Select incident type" className="h-12 border-[#C0C0C0]/25 bg-background text-base">
                <SelectValue placeholder="Select incident type…" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {INCIDENT_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={onContinue}
            disabled={!type}
            size="lg"
            className="mt-5 h-12 w-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}
